import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { epochLabel } from '@/features/projects/format'
import { useAppSelector } from '@/store'
import { selectSessionError, selectSessionLoading, selectSessionTranscript } from '../slices/intakesSlice'
import type { ConversationTurn } from '../types'

function TurnRow({ turn }: { turn: ConversationTurn }) {
  const { t } = useTranslation('projectWorkspace')
  const speaker = turn.authorName ?? t(`intakes.turnRole.${turn.role}`, { defaultValue: turn.role })
  return (
    <li className="v-row flex flex-wrap gap-x-3 gap-y-1 py-2">
      <span className="w-40 shrink-0 text-sm font-medium text-foreground">{speaker}</span>
      <time className="v-figure w-12 shrink-0 pt-0.5 font-mono text-xs text-muted-foreground">
        {epochLabel(turn.createdAt)}
      </time>
      <p className="min-w-0 flex-1 text-sm leading-6 text-foreground">{turn.content ?? ''}</p>
    </li>
  )
}

/**
 * The selected session's real conversation transcript (AC-4). A session with
 * zero turns renders its OWN empty state ("this session has not started") —
 * it never falls back to any fixture content (AC-5).
 */
export function SessionTranscriptPanel() {
  const { t } = useTranslation('projectWorkspace')
  const loading = useAppSelector(selectSessionLoading)
  const error = useAppSelector(selectSessionError)
  const transcript = useAppSelector(selectSessionTranscript)

  return (
    <section
      aria-label={t('intakes.transcript.panelTitle')}
      className="rounded-lg border border-border bg-card px-4 py-3.5"
    >
      <SectionLabel>{t('intakes.transcript.panelTitle')}</SectionLabel>
      <div className="mt-2">
        {loading ? (
          <LoadingPanel label={t('loading')} rows={2} />
        ) : error !== null ? (
          <ErrorPanel error={{ status: error.status ?? 0, backendKey: error.code, message: error.message }} />
        ) : transcript === null || transcript.turns.length === 0 ? (
          <EmptyPanel title={t('intakes.transcript.emptyTitle')} body={t('intakes.transcript.emptyBody')} />
        ) : (
          <ol data-testid="session-transcript">
            {transcript.turns.map((turn, index) => (
              <TurnRow key={turn.id ?? `${turn.role}-${index}`} turn={turn} />
            ))}
          </ol>
        )}
      </div>
    </section>
  )
}
